import React from 'react'

import theme from 'theme'

export const styles = {
  h1: {
    fontSize: theme.fontSizes[5], // 32px
    fontWeight: theme.fontWeights[1],
    lineHeight: 1.25,
    color: theme.colors.steel[2]
  },
  h2: {
    fontSize: theme.fontSizes[4], // 24px
    fontWeight: theme.fontWeights[1],
    lineHeight: 1,
    color: theme.colors.steel[2]
  },
  h3: {
    fontSize: theme.fontSizes[3], // 20px
    fontWeight: theme.fontWeights[1],
    lineHeight: 1.5,
    color: theme.colors.steel[2]
  },
  h4: {
    fontSize: theme.fontSizes[2], // 16px
    fontWeight: theme.fontWeights[1],
    lineHeight: 1.5,
    color: theme.colors.steel[2]
  },
  h5: {
    fontSize: theme.fontSizes[1], // 14px
    fontWeight: theme.fontWeights[1],
    lineHeight: 1.25,
    color: theme.colors.steel[2]
  },
  h6: {
    fontSize: theme.fontSizes[0], // 12px
    fontWeight: theme.fontWeights[1],
    lineHeight: 2,
    color: theme.colors.steel[1]
  },
  p: {
    fontSize: theme.fontSizes[1], // 14px
    fontWeight: theme.fontWeights[0],
    lineHeight: 1.5,
    color: theme.colors.steel[1]
  },
  small: {
    fontSize: theme.fontSizes[0], // 12px
    fontWeight: theme.fontWeights[0],
    lineHeight: 1.25,
    color: theme.colors.steel[1],
    display: 'block'
  }
}

export const elements = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'small',
  'span',
  'div',
  'a'
]

export const colors = {
  white: '#ffffff',
  light: theme.colors.steel[0],
  normal: theme.colors.steel[1],
  medium: theme.colors.steel[2],
  dark: theme.colors.steel[2],
  black: '#000',
  error: theme.colors.red[0],
  blue: theme.colors.blue[0],
  green: theme.colors.green[2]
}

export const weights = {
  normal: theme.fontWeights[0],
  bold: theme.fontWeights[1]
}

export interface TextProps {
  as?: React.ElementType
  overflowEllipsis?: boolean
  preserveLineBreaks?: boolean
  underline?: boolean
  lineClamp?: number
  color?:
    | 'white'
    | 'light' // theme.colors.steel[0] #bcc5db
    | 'normal' // theme.colors.steel[1] #768599
    | 'medium' // theme.colors.steel[2] #3a3f4c
    | 'dark' // theme.colors.steel[2] #222
    | 'black'
    | 'error'
    | 'blue'
    | 'green'
  weight?: 'normal' | 'bold'
  href?: string
  rel?: string
  target?: string
  to?: string
  children?: React.ReactNode
}

const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      as: Component = 'p',
      color,
      weight,
      underline,
      preserveLineBreaks = false,
      overflowEllipsis = false,
      lineClamp = 0,
      ...rest
    },
    forwardedRef
  ) => {
    const lineHeight =
      typeof Component === 'string' && Component in styles
        ? styles[Component].lineHeight
        : 1
    return (
      <Component
        ref={forwardedRef}
        css={[
          {
            padding: 0,
            margin: 0
          },

          styles[Component.toString()],

          color && { color: colors[color] },

          weight && { fontWeight: weights[weight] },

          Component === 'p' && { '& + small': { marginTop: theme.space[1] } },

          overflowEllipsis && {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },

          preserveLineBreaks && {
            whiteSpace: 'pre-wrap'
          },

          underline && {
            display: 'inline-block',
            textDecoration: 'none',
            backgroundImage:
              'linear-gradient(to bottom, currentColor 25%, currentColor 25%)',
            backgroundRepeat: 'repeat-x',
            backgroundSize: '1px 1px',
            backgroundPosition: '1px bottom',

            '&:hover': {
              color: theme.colors.blue[0],
              backgroundImage: `linear-gradient(to bottom, ${theme.colors.blue[0]} 25%, ${theme.colors.blue[0]} 25%)`
            }
          },

          Boolean(lineClamp) && {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            overflow: 'hidden',
            maxHeight: `calc(${lineHeight} * 1em * ${lineClamp})`
          },

          color === 'green' && {
            '&:hover': {
              color: theme.colors.green[1]
            }
          }
        ]}
        {...rest}
      />
    )
  }
)

Text.displayName = 'Text'

export default Text
